import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import i18n from "../i18n.js";

function LoadingFrame({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div role="status" aria-busy="true" aria-label={label} className={cn("w-full", className)}>
      {children}
    </div>
  );
}

function TableShape({
  columns,
  widths,
  rows = 5,
  avatar = false,
}: {
  columns: string[];
  widths: string[];
  rows?: number;
  avatar?: boolean;
}) {
  const template = columns.join(" ");
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div
        className="grid min-w-[640px] items-center gap-5 border-b border-border bg-muted/20 px-4 py-3"
        style={{ gridTemplateColumns: template }}
      >
        {columns.map((_, index) => (
          <Skeleton key={index} className="h-3" style={{ width: widths[index] }} />
        ))}
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }).map((_, row) => (
          <div
            key={row}
            className="grid min-w-[640px] items-center gap-5 px-4 py-3.5"
            style={{ gridTemplateColumns: template }}
          >
            {columns.map((_, column) => (
              <div key={column} className="flex min-w-0 items-center gap-3">
                {avatar && column === 0 && <Skeleton className="size-8 shrink-0 rounded-full" />}
                <Skeleton
                  className={cn("h-4 max-w-full", column === columns.length - 1 && "ml-auto")}
                  style={{ width: widths[(column + row) % widths.length] }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function PersonRows({ rows = 4, trailing = true }: { rows?: number; trailing?: boolean }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4" style={{ width: index % 2 === 0 ? "42%" : "34%" }} />
            <Skeleton className="h-3" style={{ width: index % 2 === 0 ? "61%" : "52%" }} />
          </div>
          {trailing && <Skeleton className="h-5 w-16 shrink-0 rounded-full" />}
        </div>
      ))}
    </div>
  );
}

function MetricCard({ wide = false }: { wide?: boolean }) {
  return (
    <div className="space-y-3 rounded-lg border border-border p-5">
      <Skeleton className={cn("h-4", wide ? "w-36" : "w-24")} />
      <Skeleton className="h-8 w-20" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

function FieldShape({ width = "w-full" }: { width?: string }) {
  return (
    <div className="space-y-2">
      <Skeleton className="h-3 w-24" />
      <Skeleton className={cn("h-9", width)} />
    </div>
  );
}

function CardHeading({ width = "w-40" }: { width?: string }) {
  return (
    <div className="space-y-2">
      <Skeleton className={cn("h-5", width)} />
      <Skeleton className="h-3 w-72 max-w-full" />
    </div>
  );
}

export function AccessLogSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.activities")}>
      <TableShape
        columns={["1fr", "2fr", "2fr", "1.4fr", "0.7fr"]}
        widths={["70%", "72%", "64%", "58%", "74%"]}
        avatar
      />
    </LoadingFrame>
  );
}

export function ActiveCallsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.activeCalls")}>
      <div className="overflow-hidden rounded-xl ring-1 ring-foreground/10">
        <div className="flex items-center justify-between gap-4 border-b border-border/70 bg-muted/15 p-4 sm:p-5">
          <CardHeading width="w-32" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
        <div className="grid gap-3 p-4 md:grid-cols-2 sm:p-5 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="space-y-5 rounded-xl p-4 ring-1 ring-foreground/10">
              <div className="flex items-center justify-between gap-4">
                <Skeleton className="h-5 w-24 rounded-full" />
                <Skeleton className="h-4 w-12" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-5" style={{ width: index === 1 ? "54%" : "66%" }} />
                <Skeleton className="h-4 w-36" />
              </div>
              <div className="flex items-center gap-2 border-t border-border/70 pt-3">
                <Skeleton className="size-4 rounded-full" />
                <Skeleton className="h-4 w-28" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </LoadingFrame>
  );
}

export function AiEvaluationsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.aiEvaluations")} className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <MetricCard key={index} />
        ))}
      </div>
      <div className="rounded-lg border border-border p-5">
        <CardHeading width="w-36" />
        <div className="mt-5 flex flex-wrap gap-3">
          <Skeleton className="h-6 w-32 rounded-full" />
          <Skeleton className="h-6 w-40 rounded-full" />
          <Skeleton className="h-6 w-28 rounded-full" />
        </div>
      </div>
      <TableShape
        columns={["1fr", "1fr", "0.7fr", "0.8fr", "0.7fr", "1.8fr"]}
        widths={["70%", "62%", "55%", "64%", "58%", "82%"]}
        rows={4}
      />
    </LoadingFrame>
  );
}

export function AiLeadFieldsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.leadFields")} className="mt-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-2 rounded-md border border-border/70 px-3 py-2"
          >
            <Skeleton className="size-4 rounded-sm" />
            <Skeleton className="h-4" style={{ width: index % 2 === 0 ? "58%" : "44%" }} />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function AiScriptsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.scripts")}>
      <TableShape
        columns={["1.4fr", "1fr", "0.8fr", "0.6fr", "0.9fr", "0.6fr"]}
        widths={["72%", "58%", "70%", "42%", "64%", "56%"]}
      />
    </LoadingFrame>
  );
}

export function BrandDialogSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.brands")}>
      <div className="space-y-1">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex items-center gap-3 rounded-md px-2 py-2.5">
            <Skeleton className="size-8 rounded-md" />
            <Skeleton className="h-4" style={{ width: index % 2 === 0 ? "48%" : "38%" }} />
            {index === 0 && <Skeleton className="ml-auto h-5 w-14 rounded-full" />}
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function CallFormsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.forms")}>
      <TableShape
        columns={["1.2fr", "1.8fr", "0.7fr", "0.6fr", "0.9fr", "0.6fr"]}
        widths={["70%", "80%", "72%", "38%", "64%", "55%"]}
      />
    </LoadingFrame>
  );
}

export function CallFormWizardSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.form")}>
      <div className="space-y-5 py-1">
        <FieldShape />
        <FieldShape />
        <div className="grid gap-3 sm:grid-cols-2">
          <FieldShape />
          <FieldShape />
        </div>
        <div className="space-y-3 rounded-lg border border-border p-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </LoadingFrame>
  );
}

export function CallHealthSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.callHealth")} className="space-y-6">
      {/* Herói: taxa de atendimento + veredito + KPIs, igual ao bloco alto do
          My Performance — não 4 cards de métrica soltos, layout antigo. */}
      <Skeleton className="h-48 w-full rounded-3xl" />
      <div className="rounded-lg border border-border p-5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="mt-5 h-[260px] w-full" />
      </div>
      <div className="rounded-lg border border-border p-5">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-5 h-[220px] w-full" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-border p-5">
          <Skeleton className="h-4 w-40" />
          <div className="mt-5 space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="flex items-center gap-3">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-2 flex-1 rounded-full" />
                <Skeleton className="h-4 w-12" />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-border p-5">
          <Skeleton className="h-4 w-40" />
          <div className="mt-5 space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} className="h-8 w-full" />
            ))}
          </div>
        </div>
      </div>
    </LoadingFrame>
  );
}

export function CallsReportSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.calls")}>
      <TableShape
        columns={["1.1fr", "1fr", "0.8fr", "1fr", "1fr", "0.8fr", "0.7fr"]}
        widths={["72%", "68%", "62%", "70%", "66%", "54%", "74%"]}
        rows={6}
      />
    </LoadingFrame>
  );
}

export function CampaignDetailSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.campaign")}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-5 rounded-xl border border-border p-6">
          <div className="flex items-center gap-4">
            <Skeleton className="size-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-56" />
            </div>
          </div>
          <Skeleton className="h-24 w-full" />
          <div className="flex gap-3">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>
        <div className="space-y-4 rounded-xl border border-border p-5">
          <Skeleton className="h-5 w-32" />
          <PersonRows rows={4} trailing={false} />
        </div>
      </div>
    </LoadingFrame>
  );
}

export function CapabilityDetailSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.catalog")}>
      <div className="rounded-lg border border-border">
        <div className="space-y-2 border-b border-border p-6">
          <Skeleton className="h-6 w-2/5" />
          <Skeleton className="h-4 w-3/5" />
        </div>
        <div className="space-y-0 p-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="grid grid-cols-[160px_1fr] gap-2 border-b border-border/60 py-3 last:border-0"
            >
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4" style={{ width: index % 2 === 0 ? "72%" : "48%" }} />
            </div>
          ))}
        </div>
      </div>
    </LoadingFrame>
  );
}

export function ClickAuditSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.audit")} className="space-y-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-2xl border border-border">
          <div className="flex items-start gap-4 p-5">
            <Skeleton className="size-10 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5" style={{ width: index % 2 === 0 ? "38%" : "48%" }} />
              <Skeleton className="h-4 w-2/3" />
            </div>
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
          <div className="grid gap-4 border-t border-border bg-muted/20 p-5 sm:grid-cols-3">
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-4 w-3/5" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      ))}
    </LoadingFrame>
  );
}

export function ClickHealthSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.clickToCallHealth")} className="space-y-6">
      {/* Mesmo formato da tela: herói à esquerda, perdas à direita, funil e
          gráfico embaixo. */}
      <div className="grid gap-4 xl:grid-cols-12">
        <Skeleton className="h-64 w-full rounded-3xl xl:col-span-5" />
        <div className="space-y-3 rounded-2xl border border-border p-5 xl:col-span-7">
          <Skeleton className="h-4 w-28" />
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full rounded-xl" />
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-border p-5">
        <Skeleton className="h-4 w-28" />
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-border p-5">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="mt-5 h-[260px] w-full" />
      </div>
    </LoadingFrame>
  );
}

export function CostsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.costs")} className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <MetricCard key={index} wide />
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="space-y-2 rounded-lg border border-border p-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-6 w-20" />
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border p-5">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="mt-5 h-[260px] w-full" />
      </div>
    </LoadingFrame>
  );
}

export function CostsByUserSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.userCost")} className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <MetricCard key={index} />
        ))}
      </div>
      <TableShape
        columns={["1.4fr", "0.7fr", "0.8fr", "0.9fr"]}
        widths={["70%", "42%", "55%", "60%"]}
        rows={5}
      />
    </LoadingFrame>
  );
}

export function BillingSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.monthlyAccount")} className="space-y-6">
      <div className="rounded-3xl border border-border p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-3">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="h-9 w-40" />
          </div>
          <Skeleton className="h-5 w-36 rounded-full" />
        </div>
        <div className="mt-5 grid gap-x-8 gap-y-2 border-t border-border pt-4 sm:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex items-center justify-between gap-3">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-3.5 w-16" />
            </div>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="rounded-2xl border border-border p-4">
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="mt-4 h-4 w-32" />
            <Skeleton className="mt-5 h-7 w-24" />
            <Skeleton className="mt-4 h-2.5 rounded-full" />
            <Skeleton className="mt-3 h-3 w-40" />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function PaymentSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.invoices")} className="space-y-6">
      <MetricCard />
      <TableShape
        columns={["1.4fr", "0.8fr", "0.8fr", "0.8fr"]}
        widths={["60%", "50%", "50%", "55%"]}
        rows={6}
      />
    </LoadingFrame>
  );
}

export function DialerClaimSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.nextLead")} className="min-h-[40vh]">
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 rounded-xl border border-border p-8">
        <Skeleton className="size-20 rounded-full" />
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-4 w-64 max-w-full" />
        <Skeleton className="h-11 w-40 rounded-full" />
      </div>
    </LoadingFrame>
  );
}

export function DialerStartSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.queue")} className="min-h-[40vh] flex-1">
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border p-8">
        <Skeleton className="size-14 rounded-full" />
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-72 max-w-full" />
        <Skeleton className="h-11 w-56 rounded-md" />
      </div>
    </LoadingFrame>
  );
}

export function DialerQueueSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.nextQueue")} className="px-2 py-3">
      <div className="space-y-1.5">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-3 rounded-lg border border-border/60 p-2.5"
          >
            <Skeleton className="size-8 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-5 w-10 rounded-full" />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function DispositionsReportSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.dispositionReport")} className="space-y-2">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-4" style={{ width: index % 2 ? "28%" : "36%" }} />
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="mt-3 h-2 rounded-full" style={{ width: `${88 - index * 11}%` }} />
        </div>
      ))}
    </LoadingFrame>
  );
}

export function LeadStatusesSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.status")}>
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5"
          >
            <Skeleton className="size-3 rounded-full" />
            <Skeleton className="h-4" style={{ width: index % 2 ? "32%" : "42%" }} />
            <Skeleton className="ml-auto h-8 w-20" />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function LeadsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.leads")}>
      <TableShape
        columns={["1.4fr", "1fr", "1.1fr", "0.8fr", "1fr", "0.8fr", "0.4fr"]}
        widths={["72%", "70%", "64%", "58%", "68%", "74%", "45%"]}
        rows={6}
        avatar
      />
    </LoadingFrame>
  );
}

export function McpPermissionsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.permissions")}>
      <div className="space-y-3">
        <Skeleton className="h-4 w-3/5" />
        <Skeleton className="h-4 w-4/5" />
        <Skeleton className="h-9 w-32" />
      </div>
    </LoadingFrame>
  );
}

export function McpKeysSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.apiKeys")}>
      <TableShape
        columns={["1.1fr", "0.8fr", "0.7fr", "1fr", "1fr", "0.5fr"]}
        widths={["68%", "72%", "76%", "64%", "62%", "52%"]}
        rows={3}
      />
    </LoadingFrame>
  );
}

export function ProfileSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.profile")}>
      <div className="rounded-lg border border-border">
        <div className="border-b border-border p-6">
          <CardHeading width="w-32" />
        </div>
        <div className="space-y-6 p-6">
          <div className="flex items-center gap-5">
            <Skeleton className="size-20 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-9 w-32" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <FieldShape />
            <FieldShape />
          </div>
          <FieldShape />
          <Skeleton className="ml-auto h-9 w-28" />
        </div>
      </div>
    </LoadingFrame>
  );
}

export function QueueConfigSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.queueRule")} className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {[4, 5].map((rows, card) => (
          <div key={card} className="rounded-lg border border-border p-5">
            <CardHeading width={card ? "w-36" : "w-44"} />
            <div className="mt-5 space-y-3">
              {Array.from({ length: rows }).map((_, index) => (
                <div key={index} className="grid grid-cols-[1fr_6rem] items-center gap-4">
                  <Skeleton className="h-4" style={{ width: index % 2 ? "60%" : "75%" }} />
                  <Skeleton className="h-9 w-full" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-border p-5">
        <CardHeading width="w-48" />
        <div className="mt-5 grid gap-5 sm:grid-cols-3">
          <FieldShape />
          <FieldShape />
          <FieldShape />
        </div>
      </div>
    </LoadingFrame>
  );
}

export function QueueAvailabilitySkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.availability")}>
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex items-center gap-3 rounded-lg border border-border p-3">
            <Skeleton className="size-9 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function QueueReportSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.queue")}>
      <TableShape
        columns={["0.4fr", "1.3fr", "1fr", "0.8fr", "1fr", "1fr", "0.6fr", "0.8fr", "0.7fr"]}
        widths={["40%", "72%", "68%", "72%", "58%", "64%", "44%", "62%", "70%"]}
        rows={6}
      />
    </LoadingFrame>
  );
}

export function QueueMembersSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.members")} className="p-4">
      <PersonRows rows={3} />
    </LoadingFrame>
  );
}

export function QueuesSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.queues")}>
      <TableShape
        columns={["1.3fr", "1fr", "0.7fr", "0.8fr", "0.5fr"]}
        widths={["70%", "62%", "54%", "48%", "60%"]}
        rows={5}
      />
    </LoadingFrame>
  );
}

export function TempCallRouteSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.call")}>
      <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center gap-5 rounded-2xl border border-border p-8">
        <Skeleton className="size-24 rounded-full" />
        <Skeleton className="h-6 w-52" />
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-12 w-full rounded-full" />
      </div>
    </LoadingFrame>
  );
}

export function AiConstructorRouteSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.builder")}>
      <div className="grid min-h-[75vh] gap-4 lg:grid-cols-[17rem_minmax(0,1fr)_20rem]">
        <Skeleton className="h-full" />
        <div className="relative rounded-lg border border-border">
          <Skeleton className="absolute left-1/2 top-1/3 h-28 w-48 -translate-x-1/2" />
          <Skeleton className="absolute left-1/4 top-2/3 h-24 w-44" />
        </div>
        <Skeleton className="h-full" />
      </div>
    </LoadingFrame>
  );
}

export function SellersReportSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.sellerReport")} className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <MetricCard key={index} />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-border p-5">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="mt-5 h-[240px] w-full" />
          </div>
        ))}
      </div>
      <TableShape
        columns={["1.4fr", "0.9fr", "0.7fr", "0.7fr", "0.8fr", "0.8fr", "0.8fr", "0.8fr", "0.9fr"]}
        widths={["70%", "62%", "42%", "48%", "55%", "60%", "58%", "55%", "66%"]}
        rows={4}
        avatar
      />
    </LoadingFrame>
  );
}

export function ServiceStatusSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.serviceStatus")}>
      <div className="divide-y divide-border">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex items-center gap-3 py-3">
            <Skeleton className="size-2.5 rounded-full" />
            <Skeleton className="h-4 w-24" />
            <div className="ml-auto flex gap-1">
              {Array.from({ length: 12 }).map((_, day) => (
                <Skeleton key={day} className="h-6 w-2 rounded-sm" />
              ))}
            </div>
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function SettingsDispositionsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.dispositions")}>
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="grid grid-cols-[1fr_10rem_2rem] items-center gap-3 rounded-md border border-border p-3"
          >
            <Skeleton className="h-4" style={{ width: index % 2 ? "48%" : "62%" }} />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="size-8" />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function SettingsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.organization")} className="space-y-6">
      {Array.from({ length: 4 }).map((_, card) => (
        <div key={card} className="rounded-lg border border-border">
          <div className="border-b border-border p-6">
            <CardHeading width={card % 2 ? "w-40" : "w-36"} />
          </div>
          <div className="grid gap-5 p-6 sm:grid-cols-2">
            <FieldShape />
            <FieldShape />
            {card !== 2 && <FieldShape />}
            {card === 0 && <FieldShape />}
          </div>
        </div>
      ))}
    </LoadingFrame>
  );
}

export function SipAgentsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.extensions")}>
      <TableShape
        columns={["1.5fr", "0.7fr", "0.8fr", "1fr", "0.5fr"]}
        widths={["72%", "58%", "74%", "64%", "55%"]}
        rows={5}
        avatar
      />
    </LoadingFrame>
  );
}

export function TempCallOutcomeSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.form")}>
      <div className="space-y-5">
        <FieldShape />
        <div className="space-y-3">
          <Skeleton className="h-3 w-32" />
          <div className="flex gap-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <Skeleton key={index} className="size-10 rounded-full" />
            ))}
          </div>
        </div>
        <FieldShape />
        <Skeleton className="ml-auto h-9 w-28" />
      </div>
    </LoadingFrame>
  );
}

export function TempCallSkeleton() {
  return (
    <LoadingFrame
      label={i18n.t("screenSkeletons.preparingCall")}
      className="relative w-full max-w-md"
    >
      <div className="flex flex-col items-center gap-5">
        <Skeleton className="size-24 rounded-full" />
        <Skeleton className="h-6 w-52" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-12 w-full rounded-full" />
      </div>
    </LoadingFrame>
  );
}

export function TrunkHealthSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.trunk")}>
      <div className="divide-y divide-border">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex items-center gap-3 py-3">
            <Skeleton className="size-4 rounded-full" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="ml-auto h-4 w-24" />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function TrunksSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.outboundLine")} className="max-w-2xl space-y-6">
      {Array.from({ length: 2 }).map((_, card) => (
        <div key={card} className="rounded-lg border border-border">
          <div className="border-b border-border p-6">
            <div className="flex items-center gap-3">
              <Skeleton className="size-10 rounded-lg" />
              <CardHeading width={card ? "w-36" : "w-28"} />
            </div>
          </div>
          <div className="divide-y divide-border p-6">
            {Array.from({ length: card ? 5 : 3 }).map((_, index) => (
              <div key={index} className="flex items-center gap-3 py-3">
                <Skeleton className="size-4" />
                <Skeleton className="h-4 w-28" />
                <Skeleton className="ml-auto h-4 w-32" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </LoadingFrame>
  );
}

export function UraSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.ivrs")}>
      <TableShape
        columns={["0.4fr", "1.4fr", "0.8fr", "1fr", "0.6fr"]}
        widths={["45%", "68%", "52%", "66%", "58%"]}
        rows={5}
      />
    </LoadingFrame>
  );
}

export function UsersSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.users")}>
      <TableShape
        columns={["1.7fr", "0.9fr", "1fr", "0.5fr"]}
        widths={["72%", "64%", "68%", "55%"]}
        rows={6}
        avatar
      />
    </LoadingFrame>
  );
}

export function VoicesSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.voices")}>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="rounded-lg border border-border">
            <div className="space-y-2 border-b border-border p-5">
              <Skeleton className="h-5" style={{ width: index % 2 ? "42%" : "55%" }} />
              <Skeleton className="h-3 w-4/5" />
            </div>
            <div className="space-y-4 p-5">
              <Skeleton className="h-9 w-full rounded-full" />
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-9 w-24" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function WebhookDetailSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.logDetail")}>
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <FieldShape />
          <FieldShape />
          <FieldShape />
          <FieldShape />
        </div>
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-32 w-full" />
          </div>
        ))}
      </div>
    </LoadingFrame>
  );
}

export function WebhookLogsSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.logs")}>
      <TableShape
        columns={["0.8fr", "1.2fr", "0.7fr", "0.9fr", "0.9fr", "0.8fr"]}
        widths={["68%", "72%", "48%", "64%", "66%", "74%"]}
        rows={6}
      />
    </LoadingFrame>
  );
}

export function ConversionStagesSkeleton() {
  return (
    <LoadingFrame label={i18n.t("screenSkeletons.conversionStages")}>
      <div className="space-y-6">
        <Skeleton className="h-14 w-full rounded-lg" />
        <div className="grid gap-6 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, card) => (
            <div key={card} className="space-y-4 rounded-lg border border-border p-5">
              <div className="flex items-center gap-3">
                <Skeleton className="size-9 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-48 max-w-full" />
                </div>
              </div>
              <div className="space-y-2">
                {Array.from({ length: 2 }).map((_, row) => (
                  <Skeleton key={row} className="h-10 w-full rounded-md" />
                ))}
              </div>
              <Skeleton className="h-9 w-full rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </LoadingFrame>
  );
}

// ── Molduras de rota ─────────────────────────────────────────────────────────
// Usadas como `fallback` do Suspense de cada rota lazy (ver router.tsx). O
// esqueleto de tela (UsersSkeleton, UraSkeleton, …) cobre só o CONTEÚDO, porque
// quem o renderiza é a própria tela, já dentro do seu `<main>` e abaixo do
// cabeçalho. No fallback da rota a tela inteira ainda não chegou, então a
// moldura precisa vir junto — senão o conteúdo aparece colado na borda e o
// cabeçalho empurra a página quando o chunk termina de baixar.

// Páginas internas (dentro do _app): `<main className="p-6">` + linha de título
// e ações. `children` é o esqueleto da tela; sem ele, cai num corpo genérico —
// melhor que a página em branco enquanto o chunk chega.
export function RouteScreenSkeleton({ children }: { children?: ReactNode }) {
  return (
    <main className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <Skeleton className="h-5 w-44" />
        <Skeleton className="h-9 w-32 rounded-md" />
      </div>
      {children ?? (
        <LoadingFrame label={i18n.t("ui.loading")} className="space-y-4">
          {Array.from({ length: 3 }).map((_, card) => (
            <Skeleton key={card} className="h-28 w-full rounded-lg" />
          ))}
        </LoadingFrame>
      )}
    </main>
  );
}

// Telas fullscreen fora do _app (cadastro, recuperação de senha, primeiro
// acesso, páginas legais): cartão centralado, sem sidebar nem cabeçalho.
export function FullscreenRouteSkeleton() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <LoadingFrame label={i18n.t("ui.loading")} className="max-w-[400px] space-y-5">
        <div className="flex flex-col items-center gap-3">
          <Skeleton className="size-20 rounded-full" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-3 w-56 max-w-full" />
        </div>
        <FieldShape />
        <FieldShape />
        <Skeleton className="h-9 w-full rounded-md" />
      </LoadingFrame>
    </div>
  );
}
